import * as React from 'react';
import config from '../config/config';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, FormElement, Field } from '@progress/kendo-react-form';
import {
  Button,
  ButtonGroup
} from '@progress/kendo-react-buttons';
import { Input } from './../components/form/Input';
import { MaskedTextBox } from './../components/form/MaskedTextBox';
import { Upload } from './../components/form/Upload';
import { DropDownList } from './../components/form/DropDownList';
import { Switch } from '@progress/kendo-react-inputs';
import { requiredValidator, emailValidator, phoneValidator } from './../validators';
import { Loader } from '@progress/kendo-react-indicators';
import { AppContext } from '../AppContext';

// NEU: Croppie Modal
import SignatureCroppieModal from '../components/SignatureCroppieModal';

/* ───────── Options ───────── */
const colorOptions = [
  { label: 'Braun',       value: '#A52A2A' },
  { label: 'Tomate',      value: '#FF6347' },
  { label: 'Orange',      value: '#FFA500' },
  { label: 'Grün',        value: '#9ACD32' },
  { label: 'Limette',     value: '#90EE90' },
  { label: 'Blau',        value: '#00BFFF' },
  { label: 'Navy',        value: '#000080' },
  { label: 'Violet',      value: '#6A5ACD' },
  { label: 'Pink',        value: '#FF69B4' },
  { label: 'Sand',        value: '#F4A460' },
  { label: 'Grau',        value: '#778899' },
  { label: 'Schokolade',  value: '#D2691E' }
];

const roleOptions = ['Standard Benutzer', 'Administrator'];

/* ────────────────────────────────────────────────────────────── */

const EditUser = () => {
  const { BenutzerID } = useParams();
  const navigate        = useNavigate();
  const { showNotification } = React.useContext(AppContext);

  const [userData, setUserData]                     = React.useState(null);
  const [loading, setLoading]                       = React.useState(true);
  const [saving, setSaving]                         = React.useState(false);

  /* Switches */
  const [isInPublicDirectory, setIsInPublicDirectory] = React.useState(false);
  const [autoAvatar,           setAutoAvatar]         = React.useState(false);
  const [externalEmployee,     setExternalEmployee]   = React.useState(false);
  const [befaehigungsschein,   setBefaehigungsschein] = React.useState(false);

  /* Farbauswahl */
  const [selectedColor, setSelectedColor] = React.useState(null);

  /* Benutzerrolle */
  const [employeeRole, setEmployeeRole] = React.useState(roleOptions[0]);

  /* Croppie Modal State */
  const [signatureModalOpen, setSignatureModalOpen] = React.useState(false);
  const [croppedSignatureBlob, setCroppedSignatureBlob] = React.useState(null);
  const [croppedSignaturePreview, setCroppedSignaturePreview] = React.useState(null);

  // ObjectURL sauber aufräumen
  React.useEffect(() => {
    return () => {
      if (croppedSignaturePreview) URL.revokeObjectURL(croppedSignaturePreview);
    };
  }, [croppedSignaturePreview]);

  /* ---------- Initial-Referenzen ---------- */
  const initialSwitches = React.useRef({
    active: false,
    autoAvatar: false,
    externalEmployee: false,
    befaehigungsschein: false
  });
  const initialRole = React.useRef(roleOptions[0]);

  /* ───────── Fetch User ───────── */
  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${config.host}/users/${BenutzerID}`);
        if (!res.ok) throw new Error('Benutzer nicht gefunden');

        const data = await res.json();
        setUserData(data);

        /* Active normalisieren */
        const isActive = typeof data.Active === 'string'
          ? data.Active.toLowerCase() === 'true'
          : Boolean(data.Active);
        setIsInPublicDirectory(isActive);

        /* Farbe */
        const initColor = data.UserColorCode
          ? colorOptions.find(o => o.value === data.UserColorCode)
          : null;
        setSelectedColor(initColor);

        /* Flags */
        const autoInit = Boolean(data.AutoAvatar);
        const extInit  = Boolean(data.ExternalEmployee);
        const befInit  = Boolean(data.Befaehigungsschein);
        setAutoAvatar(autoInit);
        setExternalEmployee(extInit);
        setBefaehigungsschein(befInit);

        /* Rolle */
        const initRole = data.EmployeeRole || roleOptions[0];
        setEmployeeRole(initRole);

        /* Referenzen merken */
        initialSwitches.current = {
          active: isActive,
          autoAvatar: autoInit,
          externalEmployee: extInit,
          befaehigungsschein: befInit
        };
        initialRole.current = initRole;

        // vorhandene Signatur-Preview bleibt (falls via Modal geändert)
      } catch (err) {
        console.error(err);
        showNotification('error', 'Fehler beim Laden des Benutzers.');
      } finally {
        setLoading(false);
      }
    })();
  }, [BenutzerID, showNotification]);

  /* ---------- Switch-Handler ---------- */
  const handleActiveChange     = e => setIsInPublicDirectory(e.value);
  const handleAutoAvatarChange = e => setAutoAvatar(e.value);
  const handleExternalChange   = e => setExternalEmployee(e.value);
  const handleBefaehigungsscheinChange = e => setBefaehigungsschein(e.value);

  /* ---------- Änderungen erkennen ---------- */
  const switchesChanged =
    isInPublicDirectory !== initialSwitches.current.active ||
    autoAvatar          !== initialSwitches.current.autoAvatar ||
    externalEmployee    !== initialSwitches.current.externalEmployee ||
    befaehigungsschein  !== initialSwitches.current.befaehigungsschein;

  const roleChanged = employeeRole !== initialRole.current;

  /* ---------- SPEICHERN ---------- */
  const saveUser = async (dataItem = {}) => {
    setSaving(true);
    try {
      /* Fallbacks auf userData, falls nur Switches/Rolle geändert wurden */
      const {
        firstName    = userData?.Vorname         || '',
        lastName     = userData?.Nachname        || '',
        email        = userData?.EmailAdresse    || '',
        phoneNumber  = userData?.Telefonnummer   || '',
        password     = ''
      } = dataItem;

      const formData = new FormData();

      /* Dateien nur anhängen, wenn vorhanden */
      if (dataItem?.avatar?.[0]?.getRawFile) {
        formData.append('avatar', dataItem.avatar[0].getRawFile());
      }

      // 1) Vorrang: gecroppte Signatur (Blob aus Modal)
      if (croppedSignatureBlob) {
        const fileName = `signature_${BenutzerID}.${'png'}`; // wir liefern PNG
        const file = new File([croppedSignatureBlob], fileName, { type: 'image/png' });
        formData.append('signature', file);
      }
      // 2) Falls keine Croppie-Signatur, aber Kendo-Upload verwendet wurde:
      else if (dataItem?.signature?.[0]?.getRawFile) {
        formData.append('signature', dataItem.signature[0].getRawFile());
      }

      formData.append('Benutzername', `${firstName} ${lastName}`);
      formData.append('Vorname',         firstName);
      formData.append('Nachname',        lastName);
      formData.append('EmailAdresse',    email);
      formData.append('Telefonnummer',   phoneNumber);
      formData.append('Active',          isInPublicDirectory ? 'true' : 'false');
      formData.append('UserColorCode',   selectedColor?.value || '');
      formData.append('AutoAvatar',      autoAvatar);
      formData.append('ExternalEmployee', externalEmployee);
      formData.append('Befaehigungsschein', befaehigungsschein);
      formData.append('EmployeeRole',    employeeRole);
      if (password) formData.append('Passwort', password);

      const response = await fetch(`${config.host}/users/${BenutzerID}`, {
        method: 'PUT',
        body: formData
      });
      if (!response.ok) throw new Error('Fehler beim Speichern');

      showNotification('success', 'Benutzer erfolgreich aktualisiert!');
      navigate('/users');

    } catch (err) {
      console.error('Fehler beim Aktualisieren des Benutzers:', err);
      showNotification('error', 'Fehler beim Aktualisieren des Benutzers.');
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Spinner ---------- */
  if (loading || saving) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        textAlign: 'center'
      }}>
        <Loader size="large" type="infinite-spinner" />
        <p>{loading ? 'Benutzerdaten werden geladen…' : 'Speichern – bitte warten…'}</p>
      </div>
    );
  }

  // --- Handlers für Modal ---
  const openSignatureModal = () => setSignatureModalOpen(true);
  const closeSignatureModal = () => setSignatureModalOpen(false);

  const handleSignatureSave = (blob) => {
    // Preview aktualisieren
    if (croppedSignaturePreview) URL.revokeObjectURL(croppedSignaturePreview);
    const url = URL.createObjectURL(blob);
    setCroppedSignaturePreview(url);
    setCroppedSignatureBlob(blob);
    setSignatureModalOpen(false);
    showNotification('success', 'Signatur zugeschnitten – nicht vergessen zu speichern.');
  };

  /* ───────────────────────── RENDER ───────────────────────── */
  return (
    <div id="EditUser" className="profile-page main-content">
      <div className="card-container">
        <div className="card-component">
          <Form
            initialValues={{
              firstName:   userData?.Vorname       || '',
              lastName:    userData?.Nachname      || '',
              email:       userData?.EmailAdresse  || '',
              phoneNumber: userData?.Telefonnummer || '',
              password:    ''
            }}
            onSubmit={saveUser}   /* Kendo-Submit → saveUser */
            render={(formRenderProps) => {
              /* Darf gespeichert werden? */
              const canSave =
                formRenderProps.allowSubmit ||
                switchesChanged ||
                roleChanged ||
                !!croppedSignatureBlob;

              /* Klick auf Speichern */
              const handleSaveClick = () => {
                if (formRenderProps.allowSubmit) {
                  /* Validation + Submit durch Kendo */
                  formRenderProps.onSubmit();
                } else if (switchesChanged || roleChanged || croppedSignatureBlob) {
                  /* Nur Switch(es)/Rolle/Signatur via Croppie geändert → direkt speichern */
                  saveUser({});
                }
              };

              return (
                <FormElement horizontal style={{ maxWidth: 700 }}>
                  <Field
                    id="avatar"
                    name="avatar"
                    component={Upload}
                    userImage={`${config.host}/get_image/${userData?.AvatarLink}`}
                    hideUploadButton={autoAvatar}
                  />

                  <Field
                    id="firstName"
                    name="firstName"
                    label="Vorname"
                    validator={requiredValidator}
                    component={Input}
                  />
                  <Field
                    id="lastName"
                    name="lastName"
                    label="Nachname"
                    validator={requiredValidator}
                    component={Input}
                  />
                  <Field
                    id="email"
                    name="email"
                    type="email"
                    label="E-Mail"
                    validator={emailValidator}
                    component={Input}
                  />

                  <DropDownList
                    label="Farbe"
                    id="UserColorCode"
                    name="UserColorCode"
                    data={colorOptions}
                    component={DropDownList}
                    textField="label"
                    dataItemKey="value"
                    value={selectedColor}
                    onChange={(e) => setSelectedColor(e.target.value)}
                  />

                  <Field
                    id="phoneNumber"
                    name="phoneNumber"
                    label="Telefonnummer"
                    validator={phoneValidator}
                    component={MaskedTextBox}
                  />

                  {/* Switches */}
                  <div style={{ marginTop: 20, marginLeft: 85 }}>
                    <label style={{ marginRight: 15 }}>Benutzer aktiv:</label>
                    <Switch
                      checked={isInPublicDirectory}
                      onChange={handleActiveChange}
                    />
                  </div>
                  <div style={{ marginTop: 10, marginLeft: 55 }}>
                    <label style={{ marginRight: 14 }}>Dynamisches Profil:</label>
                    <Switch
                      checked={autoAvatar}
                      onChange={handleAutoAvatarChange}
                    />
                  </div>
                  {autoAvatar && (
                    <div style={{ marginTop: 10, marginLeft: 55 }}>
                      <label style={{ marginRight: 11 }}>Externer Mitarbeiter:</label>
                      <Switch
                        checked={externalEmployee}
                        onChange={handleExternalChange}
                      />
                    </div>
                  )}
                  <div style={{ marginTop: 10, marginLeft: 55 }}>
                    <label style={{ marginRight: 16 }}>Befähigungsschein:</label>
                    <Switch
                      checked={befaehigungsschein}
                      onChange={handleBefaehigungsscheinChange}
                    />
                  </div>

                  {/* --- ButtonGroup für Benutzerrolle -------------------------------- */}
                  <div style={{ marginTop: 20, marginLeft: 86 }}>
                    <label style={{ marginRight: 15 }}>Benutzer Rolle:</label>

                    <ButtonGroup>
                      {roleOptions.map((role) => (
                        <Button
                          key={role}
                          onClick={() => setEmployeeRole(role)}
                          togglable={true}
                          selected={employeeRole === role}
                          fillMode="solid"
                        >
                          {role}
                        </Button>
                      ))}
                    </ButtonGroup>
                  </div>

                  {/* Signatur-Preview + Croppie-Trigger */}
                  <div style={{ marginTop: 30, marginLeft: 190, }}>
                    <img
                      src={
                        croppedSignaturePreview
                          ? croppedSignaturePreview
                          : `${config.host}/get_image/${userData?.UnterschriftLink}`
                      }
                      alt="User Signature"
                      style={{ width: 250, height: 150, objectFit: 'contain', border: '1px solid #eee', borderRadius: 8 }}
                    />
                    <div style={{ marginTop: 10, marginBottom: 30 }}>
                      <Button onClick={openSignatureModal}>
                        Signatur austauschen
                      </Button>
                    </div>
                  </div>

                  <Field
                    id="password"
                    name="password"
                    type="password"
                    label="Neues Passwort (leer lassen, um aktuelles zu behalten)"
                    component={Input}
                  />

                  <hr />
                  <div className="k-form-buttons">
                    <Button onClick={() => navigate('/users')}>Abbrechen</Button>
                    <Button
                      themeColor="primary"
                      type="button"
                      disabled={!canSave}
                      onClick={handleSaveClick}
                    >
                      Speichern
                    </Button>
                  </div>

                  {/* Croppie Modal */}
                  <SignatureCroppieModal
                    visible={signatureModalOpen}
                    onClose={closeSignatureModal}
                    onSave={handleSignatureSave}
                    viewportWidth={450}
                    viewportHeight={150}
                    format="png"           // PNG (Transparenz) für Signaturen
                    pngTransparent={true}
                  />
                </FormElement>
              );
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default EditUser;
